import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import type { ProduccionDiariaRow } from '@/types'

interface ChartPoint { fecha: string; qty: number }

interface Props {
  dark: boolean
  rows: ProduccionDiariaRow[]
  chartData: ChartPoint[]
}

/** Número completo con separador de millar (10.500, no «10,5k»). */
function fmtUds(n: number) {
  return n.toLocaleString('es-ES', { maximumFractionDigits: 0 })
}

function fmtDate(iso: string) {
  const [, m, d] = iso.split('-')
  return `${d}/${m}`
}

const LINE_COLORS    = ['#0ea5e9','#10b981','#f59e0b','#f43f5e','#8b5cf6']
const FAMILIA_COLORS = ['#0ea5e9','#10b981','#f59e0b','#f43f5e','#8b5cf6','#ec4899','#14b8a6','#f97316']

const W = 720, H = 240
// ML da cabida a la etiqueta del eje Y con el número completo ("1.250.000").
const ML = 76, MR = 16, MT = 18, MB = 38
const CW = W - ML - MR, CH = H - MT - MB

type Mode = 'total' | 'familia' | 'articulo'

export function ProduccionDiariaChart({ dark, rows, chartData }: Props) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const [mode, setMode] = useState<Mode>('familia')

  const cardBg  = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200'
  const txtMut  = dark ? '#475569' : '#64748b'
  const gridCol = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
  const btnBase = 'text-[10px] px-2 py-1 rounded border transition-colors font-medium cursor-pointer'
  const btnNeutral = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-400 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50`
  const btnActive = `${btnBase} bg-sky-500 border-sky-500 text-white`

  const allDates = useMemo(() =>
    [...new Set(rows.map(r => r.fecha).filter(Boolean))].sort() as string[],
  [rows])

  const singleData = chartData

  const multiArticuloData = useMemo(() => {
    const totals = new Map<string, number>()
    for (const r of rows) totals.set(r.articulo, (totals.get(r.articulo) ?? 0) + r.qtyProducida)
    const top5 = [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(e => e[0])
    return top5.map((art, i) => ({
      rawArt: art,
      label: fmtArt(art),
      color: LINE_COLORS[i],
      points: allDates.map(fecha => ({
        fecha,
        qty: rows.filter(r => r.articulo === art && r.fecha === fecha)
                 .reduce((s, r) => s + r.qtyProducida, 0),
      })),
    }))
  }, [rows, allDates, fmtArt])

  const familiaBarData = useMemo(() => {
    const totals = new Map<string, number>()
    for (const r of rows) {
      const f = r.familia ?? t('production.kpi.noFamily')
      totals.set(f, (totals.get(f) ?? 0) + r.qtyProducida)
    }
    const topFamilias = [...totals.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(e => e[0])

    const data = allDates.map(fecha => {
      const byFamilia = new Map<string, number>()
      let total = 0
      for (const r of rows) {
        if (r.fecha !== fecha) continue
        const f = topFamilias.includes(r.familia ?? '') ? (r.familia ?? t('production.kpi.noFamily')) : t('production.kpi.noFamily')
        byFamilia.set(f, (byFamilia.get(f) ?? 0) + r.qtyProducida)
        total += r.qtyProducida
      }
      return { fecha, total, byFamilia }
    })

    const maxTotal = Math.max(...data.map(d => d.total), 1)
    return { topFamilias, data, maxTotal }
  }, [rows, allDates, t])

  function xLine(i: number, n: number) {
    if (n <= 1) return ML + CW / 2
    return ML + (i / (n - 1)) * CW
  }
  function yPos(v: number, max: number) { return MT + CH - (v / max) * CH }

  function buildPath(pts: { fecha: string; qty: number }[], max: number) {
    if (!pts.length) return ''
    return pts.map((p, i) =>
      `${i === 0 ? 'M' : 'L'}${xLine(i, pts.length).toFixed(1)},${yPos(p.qty, max).toFixed(1)}`
    ).join(' ')
  }

  const nDates  = allDates.length
  const slotW   = nDates > 0 ? CW / nDates : CW
  const barW    = Math.max(2, Math.min(36, slotW * 0.68))
  function xBarLeft(i: number) { return ML + i * slotW + slotW / 2 - barW / 2 }

  function visibleDates(dates: string[]) {
    if (dates.length <= 8) return dates
    const step = Math.ceil(dates.length / 7)
    return dates.filter((_, i, arr) => i % step === 0 || i === arr.length - 1)
  }

  const maxVal = mode === 'total'
    ? Math.max(...singleData.map(p => p.qty), 1)
    : mode === 'familia'
    ? familiaBarData.maxTotal
    : Math.max(...multiArticuloData.flatMap(s => s.points.map(p => p.qty)), 1)

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({ v: maxVal * f, y: yPos(maxVal * f, maxVal) }))

  const displayDates = mode === 'total' ? singleData.map(d => d.fecha) : allDates

  if (!displayDates.length) return null

  return (
    <div className={`rounded-xl border p-4 shadow-sm ${cardBg}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <span className={`text-[10px] font-bold uppercase tracking-widest ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
          {t('production.chart.title')}
        </span>
        <div className="flex gap-1">
          <button onClick={() => setMode('total')}    className={mode === 'total'    ? btnActive : btnNeutral}>{t('production.chart.modeTotal')}</button>
          <button onClick={() => setMode('familia')}  className={mode === 'familia'  ? btnActive : btnNeutral}>{t('production.chart.modeFamily')}</button>
          <button onClick={() => setMode('articulo')} className={mode === 'articulo' ? btnActive : btnNeutral}>{t('production.chart.modeArticle')}</button>
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, display: 'block' }}>
        {yTicks.map(t_ => (
          <line key={t_.v} x1={ML} y1={t_.y} x2={ML + CW} y2={t_.y}
            stroke={gridCol} strokeWidth={1} strokeDasharray="3 3" />
        ))}
        {yTicks.map(t_ => (
          <text key={t_.v} x={ML - 6} y={t_.y + 4} textAnchor="end" fontSize={9} fill={txtMut} fontFamily="inherit">
            {fmtUds(t_.v)}
          </text>
        ))}

        {mode === 'total' && singleData.length > 0 && (() => {
          const max = Math.max(...singleData.map(p => p.qty), 1)
          const linePath = buildPath(singleData, max)
          const n = singleData.length
          const areaPath = n > 0
            ? `${linePath} L${xLine(n - 1, n).toFixed(1)},${(MT + CH).toFixed(1)} L${xLine(0, n).toFixed(1)},${(MT + CH).toFixed(1)} Z`
            : ''
          const gradId = 'totalAreaGrad'
          return (
            <>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#0ea5e9" stopOpacity={dark ? 0.28 : 0.18} />
                  <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <path d={areaPath} fill={`url(#${gradId})`} />
              <path d={linePath} fill="none" stroke="#0ea5e9" strokeWidth={2} strokeLinejoin="round" />
              {singleData.map((p, i) => (
                <circle key={p.fecha}
                  cx={xLine(i, singleData.length)} cy={yPos(p.qty, max)} r={3}
                  fill="#0ea5e9" stroke={dark ? '#1e293b' : '#fff'} strokeWidth={1.5}
                />
              ))}
            </>
          )
        })()}

        {mode === 'familia' && familiaBarData.data.map((dayData, i) => {
          let cumY = MT + CH
          return familiaBarData.topFamilias.map((fam, fi) => {
            const qty = dayData.byFamilia.get(fam) ?? 0
            if (!qty) return null
            const h = (qty / familiaBarData.maxTotal) * CH
            cumY -= h
            return (
              <rect
                key={`${dayData.fecha}-${fam}`}
                x={xBarLeft(i).toFixed(1)} y={cumY.toFixed(1)}
                width={barW} height={h.toFixed(1)}
                fill={FAMILIA_COLORS[fi % FAMILIA_COLORS.length]}
                opacity={0.88}
                rx={fi === familiaBarData.topFamilias.length - 1 ? 2 : 0}
              />
            )
          })
        })}

        {mode === 'articulo' && (() => {
          const max = Math.max(...multiArticuloData.flatMap(s => s.points.map(p => p.qty)), 1)
          return multiArticuloData.map(series => (
            <g key={series.rawArt}>
              <path d={buildPath(series.points, max)} fill="none" stroke={series.color} strokeWidth={1.5} strokeLinejoin="round" />
              {series.points.map((p, i) => (
                <circle key={p.fecha}
                  cx={xLine(i, series.points.length)} cy={yPos(p.qty, max)} r={2.5}
                  fill={series.color} stroke={dark ? '#1e293b' : '#fff'} strokeWidth={1.5}
                />
              ))}
            </g>
          ))
        })()}

        {visibleDates(displayDates).map(fecha => {
          const idx = displayDates.indexOf(fecha)
          const x = mode === 'familia'
            ? ML + idx * slotW + slotW / 2
            : xLine(idx, displayDates.length)
          return (
            <text key={fecha} x={x} y={H - 6} textAnchor="middle" fontSize={9} fill={txtMut} fontFamily="inherit">
              {fmtDate(fecha)}
            </text>
          )
        })}

        <line x1={ML} y1={MT} x2={ML} y2={MT + CH} stroke={gridCol} strokeWidth={1} />
        <line x1={ML} y1={MT + CH} x2={ML + CW} y2={MT + CH} stroke={gridCol} strokeWidth={1} />
      </svg>

      {mode === 'total' && singleData.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 rounded-full inline-block" style={{ backgroundColor: '#0ea5e9' }} />
            <span className={`text-[10px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
              {t('production.chart.legendTotal')}
            </span>
          </div>
        </div>
      )}

      {mode === 'familia' && familiaBarData.topFamilias.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5">
          {familiaBarData.topFamilias.map((fam, fi) => (
            <div key={fam} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm inline-block shrink-0"
                style={{ backgroundColor: FAMILIA_COLORS[fi % FAMILIA_COLORS.length] }} />
              <span className={`text-[10px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{fam}</span>
            </div>
          ))}
        </div>
      )}

      {mode === 'articulo' && multiArticuloData.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-2">
          {multiArticuloData.map(s => (
            <div key={s.rawArt} className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 rounded-full inline-block" style={{ backgroundColor: s.color }} />
              <span className={`text-[10px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
